import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldCheck, Ruler, Wind, Sparkles, Truck } from 'lucide-react';
import { GUIDE_BLOCKS, GUIDE_FAQ } from '../data/guideTechnique';
import JsonLd from '../components/JsonLd';
import SizeCalculator from '../components/SizeCalculator';
import { SITE_URL, SITE_NAME } from '../data/seoConfig';

const ICONS = {
  dimensionnement: Ruler,
  'normes-cts': ShieldCheck,
  'vent-cotier': Wind,
  'pack-mariage': Sparkles,
  'reservation-logistique': Truck,
};

const GuideTechniquePage = () => {
  const url = SITE_URL + '/guide-technique';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Guide technique et sécurité des barnums et chapiteaux',
    description:
      "Dimensionnement au m², normes CTS (arrêté du 23 janvier 1985), toiles M2 NF P92-507, ancrage et lestage face au vent côtier, pack mariage et conditions de réservation.",
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: GUIDE_FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Guide technique & sécurité', item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="guide-technique-page">
      <JsonLd data={articleJsonLd} />
      <JsonLd data={faqJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />

      <header className="bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-orange-400">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-orange-400">Guide technique &amp; sécurité</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5">
            Guide technique &amp; sécurité des barnums
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-2xl">
            Dimensionnement au mètre carré, conformité CTS, tenue au vent côtier, contenu du pack mariage et
            conditions de réservation : toutes les informations techniques de notre parc matériel en Normandie.
          </p>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <nav className="bg-gray-50 rounded-2xl p-6 mb-14" data-testid="guide-toc">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Sommaire</h2>
          <ol className="space-y-2 list-decimal list-inside">
            {GUIDE_BLOCKS.map((block) => (
              <li key={block.id}>
                <a href={'#' + block.id} className="text-sm text-orange-600 hover:underline">
                  {block.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {GUIDE_BLOCKS.map((block) => {
          const Icon = ICONS[block.id] || ShieldCheck;
          return (
            <section key={block.id} id={block.id} className="mb-14 scroll-mt-28" data-testid={'guide-block-' + block.id}>
              <div className="flex items-center gap-3 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-50 text-orange-600">
                  <Icon className="w-5 h-5" />
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 m-0">{block.title}</h2>
              </div>
              {block.paragraphs.map((p, i) => (
                <p key={i} className="text-base text-gray-600 leading-relaxed mb-4">{p}</p>
              ))}
            </section>
          );
        })}

        <SizeCalculator />

        <section className="mb-14" data-testid="guide-faq">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Questions fréquentes</h2>
          <div className="space-y-4">
            {GUIDE_FAQ.map((f) => (
              <div key={f.question} className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-2">{f.question}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Un doute sur la structure adaptée ?</h2>
          <p className="text-orange-100 mb-6">
            Décrivez-nous votre terrain et votre nombre d'invités : nous vous conseillons la structure, l'ancrage
            et le plancher adaptés, devis gratuit à l'appui.
          </p>
          <Link
            to="/devis"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-orange-600 font-semibold rounded-full hover:bg-orange-50 transition-colors"
            data-testid="guide-cta-devis"
          >
            Demander mon devis gratuit <ChevronRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default GuideTechniquePage;
